import { useState, useEffect, useRef, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { FormSummaryMessage, InlineFieldError } from '@/components/ui/FormFeedback';
import { SubpageHeader } from '@/components/ui/SubpageHeader';
import { fetchSystemUserInfo, getUserPostalCodes, postUpdateUserInfo, type UserGender, ApiRequestError } from '@/api';
import { formatServiceAreaSummary } from '@/constants/serviceAreas';

type ProfileFormState = {
  name: string;
  sysname: string;
  mobile: string;
  gender: UserGender;
};

type ProfileFieldErrors = {
  name?: string;
  sysname?: string;
  mobile?: string;
};

const GENDER_OPTIONS: { value: UserGender; label: string }[] = [
  { value: 0, label: 'Prefer not to say' },
  { value: 1, label: 'Male' },
  { value: 2, label: 'Female' },
];

const inputBaseClass =
  'w-full h-8 px-2.5 text-xs font-normal rounded-md border outline-none transition disabled:bg-gray-50 disabled:text-gray-400 placeholder:font-normal placeholder:text-xs placeholder:text-gray-400';

function genderOptionSelectedClasses(value: UserGender): string {
  switch (value) {
    case 0:
      return 'border-gray-400 bg-gray-100 text-gray-800 focus:ring-gray-200';
    case 1:
      return 'border-blue-500 bg-blue-50 text-blue-800 focus:ring-blue-200';
    case 2:
      return 'border-pink-500 bg-pink-50 text-pink-800 focus:ring-pink-200';
    default:
      return 'border-gray-400 bg-gray-100 text-gray-800 focus:ring-gray-200';
  }
}

function inputClass(invalid: boolean): string {
  return `${inputBaseClass} ${invalid
      ? 'border-red-300 bg-red-50/35 focus:border-red-400'
      : 'border-gray-200 bg-white focus:border-green-500'
    }`;
}

function computeProfileFieldErrors(form: ProfileFormState): ProfileFieldErrors {
  const e: ProfileFieldErrors = {};
  const nm = form.name.trim();
  if (!nm) e.name = 'Please enter your full name.';
  const mob = form.mobile.trim();
  if (mob && !/^\+?[\d\s-]{7,20}$/.test(mob)) {
    e.mobile = 'Please enter a valid phone number.';
  }
  return e;
}

export default function PersonalInfoPage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();

  const [form, setForm] = useState<ProfileFormState>({
    name: '',
    sysname: '',
    mobile: '',
    gender: 0,
  });
  const [postalCodes, setPostalCodes] = useState<string[]>(user?.postalCodes ?? []);
  const legacyPostalCodeRef = useRef(user?.postalCode?.trim() ?? '');
  const [fieldErrors, setFieldErrors] = useState<ProfileFieldErrors>({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const hydratedName = user?.name ?? '';
  const hydratedSysname = user?.sysname ?? '';
  const hydratedMobile = user?.mobile ?? '';
  const hydratedGender = (user?.gender ?? 0) as UserGender;

  useEffect(() => {
    setForm({
      name: hydratedName,
      sysname: hydratedSysname,
      mobile: hydratedMobile,
      gender: hydratedGender,
    });
  }, [hydratedName, hydratedSysname, hydratedMobile, hydratedGender]);

  useEffect(() => {
    let cancelled = false;
    void fetchSystemUserInfo()
      .then((info) => {
        if (cancelled) return;
        const codes = getUserPostalCodes(info);
        const legacyPostalCode = info.postal_code?.trim() ?? '';
        legacyPostalCodeRef.current = legacyPostalCode;
        setPostalCodes(codes);
        updateUser?.({
          postalCode: legacyPostalCode || undefined,
          postalCodes: codes,
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [updateUser]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError('');
    const fe = computeProfileFieldErrors(form);
    setFieldErrors(fe);
    if (Object.keys(fe).length > 0) {
      requestAnimationFrame(() => {
        if (fe.name) document.getElementById('profile-name')?.focus();
        else if (fe.mobile) document.getElementById('profile-phone')?.focus();
      });
      return;
    }

    const name = form.name.trim();
    const sysname = form.sysname.trim();
    const mobile = form.mobile.trim();

    setSaving(true);
    try {
      await postUpdateUserInfo({
        name,
        sysname: sysname || undefined,
        mobile: mobile || undefined,
        gender: form.gender,
        postal_code: legacyPostalCodeRef.current || undefined,
      });
      updateUser?.({
        name,
        sysname,
        mobile: mobile || undefined,
        gender: form.gender,
      });
      setFieldErrors({});
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setFormError(err instanceof ApiRequestError ? err.message : 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const field = (
    key: keyof Pick<ProfileFormState, 'name' | 'sysname' | 'mobile'>,
    label: string,
    opts?: { type?: string; placeholder?: string; inputId?: string },
  ) => {
    const inputId = opts?.inputId ?? `profile-${String(key)}`;
    const errId = `${inputId}-error`;
    const invalid = !!fieldErrors[key];
    return (
      <div>
        <label htmlFor={inputId} className="block text-xs font-normal text-gray-700 mb-1">
          {label}
        </label>
        <input
          id={inputId}
          type={opts?.type ?? 'text'}
          name={String(key)}
          value={form[key]}
          placeholder={opts?.placeholder ?? ''}
          onChange={(ev) => {
            setForm((prev) => ({ ...prev, [key]: ev.target.value }));
            setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
          }}
          aria-invalid={invalid}
          aria-describedby={invalid ? errId : undefined}
          className={inputClass(invalid)}
        />
        <InlineFieldError id={errId} message={fieldErrors[key]} />
      </div>
    );
  };

  const emailReadonlyId = 'profile-email';

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <SubpageHeader
        title="Personal Information"
        subtitle="Name, contact, and service area"
        onBack={() => navigate('/me')}
      />

      <form
        lang="en"
        noValidate
        onSubmit={handleSubmit}
        className="px-4 pt-3 space-y-2.5 pb-[calc(5.25rem+env(safe-area-inset-bottom))]"
      >
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <button
            type="button"
            onClick={() => navigate('/me/avatar')}
            className="w-full flex items-center gap-3 cursor-pointer text-left"
          >
            <div className="relative w-14 h-14 rounded-full bg-green-700 flex items-center justify-center overflow-hidden group flex-shrink-0 border-2 border-white shadow">
              {(() => {
                const url = user?.avatarUrl?.trim();
                if (url) {
                  return (
                    <img
                      src={url}
                      alt="Avatar"
                      className="w-full h-full rounded-full object-cover"
                    />
                  );
                }
                const n = (user?.name ?? '').trim();
                const letter = n ? n[0].toUpperCase() : 'ME';
                return <span className="text-white font-bold text-sm">{letter}</span>;
              })()}
              <div className="absolute inset-0 rounded-full bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <i className="ri-camera-line text-white text-sm" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-gray-900 font-semibold text-sm">Profile Photo</div>
              <div className="text-gray-400 text-xs mt-0.5">Tap to change your avatar</div>
            </div>
            <i className="ri-arrow-right-s-line text-gray-300 text-xl flex-shrink-0" />
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-2.5">
          <div>
            <label htmlFor={emailReadonlyId} className="block text-xs font-normal text-gray-700 mb-1">
              Email
            </label>
            <input
              id={emailReadonlyId}
              type="email"
              readOnly
              value={user?.email ?? ''}
              className={`${inputBaseClass} border-gray-100 bg-gray-50 text-gray-400`}
            />
          </div>

          {field('name', 'Full Name', { placeholder: 'Enter your full name', inputId: 'profile-name' })}
          {field('sysname', 'System Name', {
            placeholder: 'Enter your system name',
            inputId: 'profile-system-name',
          })}
          {field('mobile', 'Phone Number', {
            type: 'tel',
            placeholder: '+1 234 567 8900',
            inputId: 'profile-phone',
          })}

          <button
            type="button"
            onClick={() => navigate('/me/service-areas')}
            className="w-full flex items-center gap-2.5 text-left cursor-pointer"
          >
            <div className="flex-1 min-w-0">
              <div className="text-xs font-normal text-gray-700">Service Area Postal Code</div>
              <div className="text-gray-400 text-[11px] mt-0.5 truncate">
                {formatServiceAreaSummary(postalCodes)}
              </div>
            </div>
            <i className="ri-arrow-right-s-line text-gray-300 text-xl flex-shrink-0" />
          </button>

          <div>
            <span
              id="profile-gender-label"
              className="block text-[10px] font-normal text-gray-700 mb-1"
            >
              Gender
            </span>
            <div
              className="flex flex-wrap gap-1"
              role="radiogroup"
              aria-labelledby="profile-gender-label"
            >
              {GENDER_OPTIONS.map((opt) => {
                const selected = form.gender === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setForm((prev) => ({ ...prev, gender: opt.value }))}
                    className={`h-6 px-2 text-[10px] font-medium rounded border transition focus:outline-none focus:ring-1 cursor-pointer leading-none ${selected
                        ? genderOptionSelectedClasses(opt.value)
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 focus:border-gray-300 focus:ring-gray-100'
                      }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <FormSummaryMessage message={formError} />

        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="mx-auto w-full" style={{ maxWidth: 480 }}>
            <button
              type="submit"
              disabled={saving}
              className="w-full h-9 rounded-lg bg-green-500 hover:bg-green-600 text-white font-medium text-xs cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-sm" />
                  Saving…
                </>
              ) : saved ? (
                <>
                  <i className="ri-check-line text-sm" />
                  Saved
                </>
              ) : (
                <>
                  <i className="ri-save-line text-sm" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {saved && (
        <div className="fixed top-16 left-0 right-0 z-50 flex justify-center px-4 pointer-events-none">
          <div className="bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-md shadow-lg flex items-center gap-2">
            <i className="ri-checkbox-circle-fill text-green-400" />
            Changes saved successfully
          </div>
        </div>
      )}
    </div>
  );
}
